import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GET_ALL_VENDORS_API, UPDATE_INWARD_API, GET_PO_LIST_API, CREATE_NEW_INWARD_API, GET_PENDING_AND_PARTIAL_PO_API, GET_PENDING_AND_PARTIAL_PO_DETAILS_API } from '../config/API';
import { MdArrowBack, MdCloudUpload, MdCheckCircle, MdPhotoCamera } from 'react-icons/md';
import Swal from 'sweetalert2';
import Loader from '../components/common/Loader';
import PaginatedSelect from '../components/common/PaginatedSelect';
import './InwardEntry.css';
import { formatDate } from '../utils/dateUtils';

const InwardEntry = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Selection States
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [selectedPo, setSelectedPo] = useState(null);

    // PO Data States
    const [poData, setPoData] = useState(null);
    const [items, setItems] = useState([]);

    // Form States
    const [challanFile, setChallanFile] = useState(null);
    const [challanPreview, setChallanPreview] = useState(null);
    const [remarks, setRemarks] = useState('');
    const [globalStatus, setGlobalStatus] = useState('pending');
    const [inwardNo, setInwardNo] = useState('');
    const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'camera'

    // Initial data is handled by PaginatedSelect
    useEffect(() => {
        if (id) {
            // If editing, we might need to fetch initial data
            // But this page handles both create and edit (via id param)
        }
    }, [id]);

    // Filter POs logic is now handled via API params in PaginatedSelect

    useEffect(() => {
        const fetchPoDetails = async () => {
            if (!selectedPo || id) return;

            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.get(GET_PENDING_AND_PARTIAL_PO_DETAILS_API(selectedPo.value), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    const data = response.data.data.request || response.data.data;
                    setPoData(data);

                    // Map items for initial entry
                    const poItems = data.PurchaseOrderItems || [];
                    setItems(poItems.map(item => ({
                        id: item.id,
                        productId: item.productId,
                        productName: item.Product?.productName || item.productDescription,
                        orderedQty: item.qty || 0,
                        receivedTotal: item.totalReceived || 0,
                        pendingQty: item.pendingQuantity || 0,
                        receiveNow: '',
                        unitName: item.Unit?.unitName || item.Product?.Unit?.unitName || 'Nos',
                        unitId: item.unitId || item.Unit?.id || item.Product?.unitId || 1
                    })));
                }
            } catch (error) {
                console.error('Error fetching PO details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPoDetails();
    }, [selectedPo, id]);

    // Handle File Upload
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setChallanFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setChallanPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveFile = () => {
        setChallanFile(null);
        setChallanPreview(null);
        const fileInput = document.getElementById('challan-upload');
        const cameraInput = document.getElementById('camera-upload');
        if (fileInput) fileInput.value = '';
        if (cameraInput) cameraInput.value = '';
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const handleSubmit = async () => {
        if (!selectedVendor || !selectedPo) {
            Swal.fire('Missing Selection', 'Please select a Vendor and a Purchase Order.', 'warning');
            return;
        }

        const result = await Swal.fire({
            title: `Confirm Inward Entry?`,
            text: `Process inward for PO ${selectedPo.label}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `Yes, Confirm`,
            confirmButtonColor: '#6366f1'
        });
        if (result.isConfirmed) {
            setSubmitting(true);
            try {
                const token = localStorage.getItem('accessToken');
                const formData = new FormData();
                formData.append('poId', Number(selectedPo.value));
                formData.append('vendorId', Number(selectedVendor.value));

                // Robust projectId extraction
                const projectId = poData?.projectId ||
                    poData?.ProjectMaster?.id ||
                    selectedPo.allData?.ProjectMaster?.id ||
                    0;

                formData.append('projectId', Number(projectId));
                formData.append('date', "");
                formData.append('remark', remarks);

                if (challanFile) {
                    formData.append('challanFile', challanFile);
                } else if (challanPreview && typeof challanPreview === 'string' && !challanPreview.startsWith('data:')) {
                    // If it's an existing file URL during edit
                    formData.append('challanFile', challanPreview);
                } else {
                    formData.append('challanFile', "");
                }

                formData.append('inwardItems', JSON.stringify(items.map(item => ({
                    productId: Number(item.productId),
                    unitId: Number(item.unitId || 1),
                    receiveNow: Number(item.receiveNow)
                }))));

                const apiUrl = id ? UPDATE_INWARD_API(id) : CREATE_NEW_INWARD_API;
                const response = await axios.post(apiUrl, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.data.status) {
                    Swal.fire({
                        title: 'Success!',
                        text: `Inward entry created successfully.`,
                        icon: 'success'
                    }).then(() => {
                        navigate('/inward-management');
                    });
                } else {
                    Swal.fire('Error', response.data.error || response.data.message || 'Failed to process inward.', 'error');
                }

            } catch (error) {
                console.error('Error submitting inward:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'Failed to submit inward entry.', 'error');
            } finally {
                setSubmitting(false);
            }
        }
    };



    return (
        <div className="inward-entry-page">
            <div className="page-header">
                <button className="back-btn" onClick={() => navigate('/inward-management')}>
                    <MdArrowBack size={20} />
                </button>
                <h2>Create Inward Entry</h2>
            </div>

            <div className="inward-form-card">
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="form-section">
                        <h3>Selection Details</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Vendor <span className="required-star">*</span></label>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_VENDORS_API}
                                    mapResponse={(v) => ({
                                        value: v.id || v._id,
                                        label: `${v.vendorName} (${v.companyName || 'N/A'})`,
                                        allData: v
                                    })}
                                    value={selectedVendor}
                                    onChange={(val) => {
                                        setSelectedVendor(val);
                                        setSelectedPo(null);
                                    }}
                                    placeholder="Select Vendor"
                                    isDisabled={!!id}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="form-group">
                                <label>Purchase Order <span className="required-star">*</span></label>
                                <PaginatedSelect
                                    apiUrl={GET_PENDING_AND_PARTIAL_PO_API}
                                    params={{ vendorId: selectedVendor?.value }}
                                    mapResponse={(po) => ({
                                        value: po.id,
                                        label: `${po.poNumber || po.id} (${po.companyName || po.vendorName || 'N/A'})`,
                                        allData: po
                                    })}
                                    value={selectedPo}
                                    onChange={setSelectedPo}
                                    placeholder={selectedVendor ? "Select PO" : "Select Vendor First"}
                                    isDisabled={!selectedVendor || !!id}
                                    key={selectedVendor?.value || 'no-vendor'}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </div>

                            {(poData || (id && selectedPo)) && (
                                <>
                                    <div className="form-group">
                                        <label>PO Date</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formatDate(poData?.date || selectedPo?.allData?.date)}
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Project Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={
                                                poData?.ProjectMaster?.projectName ||
                                                poData?.Project?.projectName ||
                                                selectedPo?.allData?.ProjectMaster?.projectName ||
                                                selectedPo?.allData?.Project?.projectName ||
                                                'N/A'
                                            }
                                            disabled
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Delivery Challan & Details</h3>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label>Challan Document {!id && <span className="required-star">*</span>}</label>
                                
                                <div className="upload-tabs">
                                    <button 
                                        type="button" 
                                        className={`tab-btn ${uploadMethod === 'file' ? 'active' : ''}`}
                                        onClick={() => setUploadMethod('file')}
                                    >
                                        <MdCloudUpload size={18} /> Upload File
                                    </button>
                                    <button 
                                        type="button" 
                                        className={`tab-btn ${uploadMethod === 'camera' ? 'active' : ''}`}
                                        onClick={() => setUploadMethod('camera')}
                                    >
                                        <MdPhotoCamera size={18} /> Take Photo
                                    </button>
                                </div>

                                <div 
                                    className={`challan-upload-zone ${uploadMethod === 'camera' ? 'active-camera' : ''}`} 
                                    onClick={() => {
                                        if (uploadMethod === 'file') {
                                            document.getElementById('challan-upload').click();
                                        } else {
                                            document.getElementById('camera-upload').click();
                                        }
                                    }}
                                >
                                    <input
                                        type="file"
                                        accept="image/*,.pdf"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        id="challan-upload"
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        id="camera-upload"
                                    />
                                    <div className="upload-icon-wrapper">
                                        {uploadMethod === 'file' ? (
                                            <>
                                                <MdCloudUpload size={48} color="#6366f1" />
                                                <span className="upload-text-primary">
                                                    {challanFile ? challanFile.name : (id && challanPreview ? 'Click to Change Challan' : 'Click to Upload Challan')}
                                                </span>
                                                <span className="upload-text-secondary">Supports JPG, PNG, PDF</span>
                                            </>
                                        ) : (
                                            <>
                                                <MdPhotoCamera size={48} color="#10b981" />
                                                <span className="upload-text-primary" style={{ color: '#065f46' }}>
                                                    {challanFile ? 'Photo Captured' : 'Click to Open Camera'}
                                                </span>
                                                <span className="upload-text-secondary">Selfie or Document Scan</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {challanPreview && typeof challanPreview === 'string' && (
                                    <div className="preview-container">
                                        <div className="preview-header">
                                            <span className="preview-label">File Preview:</span>
                                            <div className="preview-actions">
                                                <button
                                                    type="button"
                                                    className="preview-action-btn view-full-btn"
                                                    onClick={() => window.open(challanPreview, '_blank')}
                                                >
                                                    View Full Screen
                                                </button>
                                                <button
                                                    type="button"
                                                    className="preview-action-btn remove-file-btn"
                                                    onClick={handleRemoveFile}
                                                >
                                                    Remove File
                                                </button>
                                            </div>
                                        </div>
                                        {(challanFile?.type?.startsWith('image/') || (typeof challanPreview === 'string' && (challanPreview.startsWith('data:image') || challanPreview.match(/\.(jpg|jpeg|png)$/i)))) ? (
                                            <img src={challanPreview} alt="Preview" className="challan-preview-img" />
                                        ) : (challanFile?.type === 'application/pdf' || (typeof challanPreview === 'string' && (challanPreview.startsWith('data:application/pdf') || challanPreview.match(/\.pdf$/i)))) ? (
                                            <div className="pdf-preview-wrapper">
                                                <iframe
                                                    src={challanPreview}
                                                    title="Challan PDF Preview"
                                                    className="challan-pdf-iframe"
                                                ></iframe>
                                            </div>
                                        ) : (
                                            <div className="challan-file-badge">Challan File Uploaded</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="form-group full-width">
                                <label>Remarks / Notes</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    placeholder="Any remarks regarding delivery..."
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {items.length > 0 && (
                        <div className="form-section">
                            <h3>Items Verification</h3>
                            <div className="items-table-wrapper">
                                <table className="items-table">
                                    <thead>
                                        <tr>
                                            <th>Item Details</th>
                                            <th>Ordered Qty</th>
                                            <th>Received (Total)</th>
                                            <th>Pending Qty</th>
                                            <th style={{ width: '150px' }}>Receive (Now)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr key={index}>
                                                <td className="item-name-cell">
                                                    {item.productName}
                                                </td>
                                                <td className="qty-display">{item.orderedQty + ' ' + (item.unitName || 'Nos')}</td>
                                                <td>{item.receivedTotal || 0}</td>
                                                <td>{item.pendingQty || 0}</td>
                                                <td className="receive-now-cell">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        className="form-control"
                                                        value={item.receiveNow}
                                                        onChange={(e) => handleItemChange(index, 'receiveNow', e.target.value)}
                                                        max={item.pendingQty || 0}
                                                    />
                                                    <span className="unit-label">{item.unitName || 'Nos'}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile View */}
                            <div className="mobile-items-view">
                                {items.map((item, index) => (
                                    <div key={index} className="mobile-item-card">
                                        <div className="mobile-item-header">
                                            <div className="mobile-item-title">
                                                <span className="sr-badge">Item {index + 1}</span>
                                                <strong>{item.productName}</strong>
                                            </div>
                                        </div>
                                        <div className="mobile-item-body">
                                            <div className="mobile-item-row">
                                                <span className="m-label">Ordered Qty</span>
                                                <span className="m-value">{item.orderedQty} {item.unitName || 'Nos'}</span>
                                            </div>
                                            <div className="mobile-item-row">
                                                <span className="m-label">Received Total</span>
                                                <span className="m-value">{item.receivedTotal || 0} {item.unitName || 'Nos'}</span>
                                            </div>
                                            <div className="mobile-item-row">
                                                <span className="m-label">Pending Qty</span>
                                                <span className="m-value">{item.pendingQty || 0} {item.unitName || 'Nos'}</span>
                                            </div>
                                            <div className="mobile-item-row full-width">
                                                <span className="m-label">Receive Now ({item.unitName || 'Nos'})</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="form-control"
                                                    value={item.receiveNow}
                                                    onChange={(e) => handleItemChange(index, 'receiveNow', e.target.value)}
                                                    max={item.pendingQty || 0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="form-actions">
                        <button type="button" className="cancel-btn" onClick={() => navigate('/inward-management')}>
                            Cancel
                        </button>
                        <button type="button" className="save-btn" onClick={handleSubmit} disabled={submitting || (!id && !selectedPo)}>
                            {submitting ? 'Processing...' : (
                                <>
                                    <MdCheckCircle size={20} /> {id ? 'Update' : 'Confirm'} Inward
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InwardEntry;
